/* eslint-disable react-hooks/rules-of-hooks */

import { memo, useEffect, useRef } from "react";
import isEqual from "react-fast-compare";

import { Trash2 } from "lucide-react";
import { ChatMessageWithSender } from "@/libs/api/types";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/cn";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getInitials } from "../utils";

interface ChatMessageProps {
  message: ChatMessageWithSender;
  conversationId: string;
  content?: string;
  senderName?: string;
  avatarUrl?: string | null;
  senderId: string;
  currentUserId: string | undefined;
  createdAt?: Date;
  onMarkAsRead: (messageId: string, conversationId: string) => void;
}

export const ChatMessage = memo(_ChatMessage, isEqual);

function _ChatMessage({
  message,
  currentUserId,
  onMarkAsRead,
}: ChatMessageProps) {
  const { t } = useTranslation();

  const messageRef = useRef<HTMLDivElement>(null);

  const {
    id: messageId,
    messageType,
    senderName,
    fileUrl,
    conversationId,
    senderId,
    createdAt,
    senderAvatarUrl,
    content,
    isRead,
  } = message;
  const isUserSender = currentUserId === senderId;

  useEffect(() => {
    if (isUserSender || isRead) {
      return;
    }

    const element = messageRef.current;
    if (!element) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            onMarkAsRead(messageId, conversationId);
          }
        });
      },
      { threshold: 0.5 },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [isUserSender, isRead, messageId, conversationId, onMarkAsRead]);

  return (
    <div
      ref={messageRef}
      className={cn("flex gap-3 group", {
        "flex-row-reverse": isUserSender,
        "flex-row": !isUserSender,
      })}
    >
      <Avatar className="h-10 w-10 flex-shrink-0 border">
        <AvatarImage src={senderAvatarUrl} alt={senderName} />
        <AvatarFallback className="text-gray-400 text-sm font-medium">
          {getInitials(senderName)}
        </AvatarFallback>
      </Avatar>

      <div
        className={cn("flex flex-col gap-1 max-w-[70%]", {
          "items-end": isUserSender,
          "items-start": !isUserSender,
        })}
      >
        <div
          className={cn("flex items-center gap-2 px-1", {
            "flex-row-reverse": isUserSender,
            "flex-row": !isUserSender,
          })}
        >
          <span className="text-sm font-semibold text-slate-800">
            {senderName}
          </span>
          <span className="text-sm text-muted-foreground">
            {createdAt ? format(new Date(createdAt), "HH:mm") : ""}
          </span>
        </div>

        <div
          className={cn("relative px-4 py-3 rounded-md", {
            "bg-blue-700 text-primary-foreground rounded-tr-xs": isUserSender,
            "bg-gray-100 text-slate-800 rounded-tl-xs": !isUserSender,
          })}
        >
          <ChatMessageContent
            messageType={messageType}
            content={content}
            imageUrl={fileUrl}
          />

          {isUserSender && (
            <div className="flex items-center justify-end gap-1 mt-1">
              <span className="text-sm text-primary-foreground/70 italic">
                {isRead ? t("chat.messageRead") : t("chat.messageDelivered")}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function ChatMessageDateLabel({ date }: { date: Date }) {
  return (
    <div className="flex items-center justify-center my-4 sticky top-0 z-10">
      <h2 className="px-4 py-1 bg-gray-100 rounded-full text-[14px] text-gray-600 font-semibold">
        {format(date, "EEEE, d. MMMM yyyy")}
      </h2>
    </div>
  );
}

interface ChatMessageContentProps {
  messageType: string;
  content?: string;
  imageUrl?: string;
}

function ChatMessageContent({
  messageType,
  content,
  imageUrl,
}: ChatMessageContentProps) {
  //const isImage = fileName?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
  //const isPdf = fileName?.match(/\.pdf$/i);

  switch (messageType) {
    case "text":
      return <span className="break-words">{content}</span>;
    case "media":
      return (
        <div className="flex flex-col gap-2 min-w-[300px] max-w-md">
          <div className="relative rounded-sm overflow-hidden bg-white/10">
            <img
              src={imageUrl}
              alt={content || "Image"}
              className="w-full h-auto max-h-[500px] object-contain rounded-sm"
            />
          </div>
          {content && <span className="break-words">{content}</span>}
        </div>
      );
  }
}
